import { /*Tooltip,*/ TreeSelect, InputNumber, ColorPicker, Tooltip } from 'antd'
import classNames from 'classnames'
import React, { type InputHTMLAttributes, type PropsWithChildren, type ReactElement, type ReactNode, useState } from 'react'
import { type Control, Controller } from 'react-hook-form'
import { CheckboxInner } from './checkbox'
import { DatePickerInner } from './datePicker'
import { FormField, type FormFieldProps, type LabelPosition } from './formField'
import { RadioInner } from './radio'
import { SelectInner } from './select'
import { TimePickerInner } from './timePicker'
import useTranslation from 'utils/hooks/useTranslation'
import { SvgChevronDown, SvgChevronUp } from 'components/svg'

export const InputType = {
  Text: 'text',
  Password: 'password',
  DatePicker: 'datepicker',
  TimePicker: 'timePicker',
  Select: 'select',
  TreeSelect: 'TreeSelect',
  Checkbox: 'Checkbox',
  Radio: 'Radio',
  TextArea: 'textarea',
  Number: 'number',
  ColorPicker: 'colorPicker',
}
export interface UncontrolledInputProps extends Omit<FormFieldProps, 'value'>, InputHTMLAttributes<HTMLInputElement> {
  type?: string
  label?: string | ReactElement
  endLabel?: string
  addonAfter?: ReactNode
  controls?: any
  suffix?: ReactNode
  inputClassName?: string
  translateLabel?: boolean
  requiredLabel?: boolean
  icon?: any
  customIcon?: any
  inputBox?: boolean
  minimal?: boolean
  button?: ReactElement
  rules?: any
  name?: string
  control?: never
  invalid?: any
  isTouched?: any
  isDirty?: any
  error?: any
  size?: any
  fixLength?: string
  labelPosition?: LabelPosition
  disabledAlpha?: boolean
  showText?: any
  translateValue?: boolean
  minValue?: number
  maxValue?: number
  stringCheckedValue?: string
  onCustomChange?: (name: string, value: any) => void
  mode?: 'multiple' | 'tags'
}
export interface ControlledInputProps extends Omit<UncontrolledInputProps, 'control' | 'name'> {
  name: string
  control: Control<any, any>
}
export type InputProps = (ControlledInputProps | UncontrolledInputProps) & {
  confirmEdit?: boolean
  dataMaterial?: boolean
}

const Input: React.FC<InputProps> = React.forwardRef<ReactElement, PropsWithChildren<InputProps>>((props, ref) => {
  const { name, control, ...restProps } = props as any
  return control ? (
    <Controller
      defaultValue={restProps.defaultValue}
      control={control}
      name={name}
      render={({ field: { onChange, value, ref }, fieldState: { error, isDirty, invalid, isTouched } }) => (
        <InputInner
          ref={ref}
          isDirty={isDirty}
          invalid={invalid}
          isTouched={isTouched}
          value={value}
          error={error}
          onChange={onChange}
          name={name}
          {...restProps}
        />
      )}
    />
  ) : (
    <InputInner ref={ref} name={name} {...restProps} />
  )
})

const InputInner: React.FC<InputProps> = React.forwardRef<ReactElement, PropsWithChildren<InputProps>>(
  (
    {
      type = InputType.Text,
      className,
      label = '',
      translateLabel = true,
      requiredLabel = false,
      icon,
      customIcon,
      inputBox = false,
      placeholder = ' ',
      button,
      invalid,
      isTouched,
      isDirty,
      error,
      //fixLength,
      dataLength,
      dataMaxLength,
      labelPosition,
      inputClassName,
      //confirmEdit,
      borderLess,
      minValue,
      maxValue,
      ...rest
    },
    ref,
  ) => {
    //const { t } = useTranslation();
    //const [confirmed, setConfirmed] = useState(false);
    //const { confirm } = useConfirm();

    let input
    const inputProps = {
      ...rest,
    }
    const size = 'compact'
    const antClassName = 'form_field_ant'
    let isAnt = false

    switch (type) {
      case InputType.DatePicker:
        isAnt = true
        input = (
          <DatePickerInner
            ref={ref}
            placeholder={placeholder}
            size={size}
            //bordered={false}
            variant="borderless"
            className={classNames(antClassName, inputClassName)}
            {...(inputProps as any)}
          />
        )
        break
      case InputType.TimePicker:
        isAnt = true
        input = (
          <TimePickerInner
            ref={ref}
            //bordered={false}
            variant="borderless"
            placeholder={placeholder}
            size={size}
            showNow={false}
            className={classNames(antClassName, inputClassName)}
            {...(inputProps as any)}
          />
        )
        break
      case InputType.Select:
        isAnt = true
        // if (['multiple', 'tags'].includes((inputProps as SelectProps).mode || '')) {
        //     hasValue = (value as any).length > 0;
        // }
        input = (
          <SelectInner
            {...(inputBox ? { showAction: ['focus'] } : null)}
            ref={ref}
            placeholder={placeholder}
            size={size}
            //bordered={false}
            variant="borderless"
            showAction={'focus'}
            className={classNames(antClassName, inputClassName)}
            autoComplete={'new-password'}
            suffixIcon={(
              <icon data-svg-size="fit" data-length="10" data-svg-color="">
                <SvgChevronDown />
              </icon>
            )}
            // controls={{
            //   upIcon: (
            //     <icon data-svg-size="fit" data-length="14" data-svg-color="secondary">
            //       <SvgChevronUp />
            //     </icon>
            //   ),
            //   downIcon: (
            //     <icon data-svg-size="fit" data-length="14" data-svg-color="secondary">
            //       <SvgChevronDown />
            //     </icon>
            //   ),
            // }}
            {...(inputProps as any)}
            virtual={(inputProps as any).virtual ?? false}
          />
        )
        break
      case InputType.TreeSelect:
        isAnt = true
        input = (
          <TreeSelect
            {...(inputBox ? { showAction: ['focus'] } : null)}
            ref={ref}
            //bordered={false}
            variant="borderless"
            placeholder={placeholder}
            size={size}
            showAction={'focus'}
            className={classNames(antClassName, inputClassName)}
            {...(inputProps as any)}
          />
        )
        break
      case InputType.Checkbox:
        input = (
          <CheckboxInner
            ref={ref}
            placeholder={placeholder}
            size={size}
            error={error}
            invalid={invalid}
            isTouched={isTouched}
            icon={icon}
            label={label}
            className={className}
            {...(inputProps as any)}
          />
        )
        break
      case InputType.Radio:
        input = (
          <RadioInner
            ref={ref}
            placeholder={placeholder}
            size={size}
            error={error}
            invalid={invalid}
            isTouched={isTouched}
            icon={icon}
            className={className}
            label={label}
            {...(inputProps as any)}
          />
        )
        break
      case InputType.Number:
        isAnt = true
        input = (
          <InputNumber
            ref={ref}
            //bordered={false}
            variant="borderless"
            className={classNames(antClassName, inputClassName)}
            placeholder={placeholder}
            controls={{
              upIcon: (
                <icon data-svg-size="fit" data-length="16" data-svg-color="secondary">
                  <SvgChevronUp />
                </icon>
              ),
              downIcon: (
                <icon data-svg-size="fit" data-length="16" data-svg-color="secondary">
                  <SvgChevronDown />
                </icon>
              ),
            }}
            min={minValue ?? -1000000}
            max={maxValue ?? 1000000}
            maxlength="10"
            formatter={(value) => {
              if (value === null || value === undefined || value === '') return ''
              const num = parseFloat(value.toString())
              return Number.isInteger(num) ? num.toString() : num.toString()
            }}
            {...(inputProps as any)}
          />
        )
        // input = (
        //   <input
        //     type="number"
        //     ref={ref}
        //     placeholder={placeholder}
        //     {...(inputProps as any)}
        //   />
        // );
        break
      case InputType.TextArea:
        input = <textarea ref={ref} placeholder={placeholder} {...(inputProps as any)} />
        break
      case InputType.ColorPicker:
        input = (
          <ColorPicker
            arrow={false}
            // ref={ref}
            placeholder={placeholder}
            {...(inputProps as any)}
            onChange={(e) => {
              inputProps.onChange?.(e.toHexString() as any)
            }}
            onMouseDown={(e: any) => {
              e.stopPropagation()
            }}
          />
        )
        break
      default:
        input = (
          <input
            type={type}
            ref={ref as any}
            placeholder={placeholder}
            {...(type === 'password' ? { autoComplete: 'new-password' } : null)}
            {...inputProps}
          // onChange={
          //   confirmEdit && !confirmed
          //     ? () => {
          //         confirm({
          //           message: t("#000002050"),
          //           positiveCallback: () => setConfirmed(true),
          //         });
          //       }
          //     : inputProps.onChange
          // }
          />
        )
    }

    const content = ![InputType.Checkbox, InputType.Radio].includes(type) ? (
      <FormField
        className={classNames(className, {
          //   hasValue: (!isTouched && inputProps.defaultValue) || hasValue,
          error: invalid,
          touched: isTouched,
          dirty: isDirty,
        })}
        labelSize={inputProps.labelSize}
        value={inputProps.value}
        label={label}
        translateLabel={translateLabel}
        requiredLabel={requiredLabel}
        button={button}
        icon={icon}
        customIcon={customIcon}
        dataLength={dataLength}
        dataMaxLength={dataMaxLength}
        isAnt={isAnt}
        labelPosition={labelPosition}
        error={error?.message}
        borderLess={borderLess}
        size={rest.size}
        info={inputProps.info}
        helpText={inputProps.helpText}
        unit={inputProps.unit}
        emptyClass={inputProps.emptyClass}
        placeholder={placeholder}
        minValue={minValue}
        maxValue={maxValue}
        dataMaterial={inputProps.dataMaterial}
      // onClick={
      //   rest.disabled && !confirmed && confirmEdit
      //     ? () => {
      //         confirm({
      //           message: t("#000002050"),
      //           positiveCallback: () => setConfirmed(true),
      //         });
      //       }
      //     : undefined
      // }
      >
        {input}
      </FormField>
    ) : (
      input
    )

    return content
  },
)

export const Password: React.FC<InputProps> = React.forwardRef<ReactElement, PropsWithChildren<InputProps>>((props, ref) => {
  const [show, setShow] = useState(false)
  const [blurTimeout, setBlurTimeout] = useState(setTimeout(() => { }))
  const { t } = useTranslation()
  const onBlur = (e: any) => {
    const nativeEvent = e.nativeEvent
    setBlurTimeout(
      setTimeout(() => {
        if (props.onBlur) {
          return props.onBlur(nativeEvent)
        }
      }, 300),
    )
  }
  return (
    <Input
      ref={ref}
      type={show ? 'text' : 'password'}
      button={
        <>
          <separator data-vertical="" data-height="input"></separator>
          <Tooltip arrow={false} title={show ? t('HidePassword') : t('ShowPassword')}>
            <div //NOSONAR
              className="button"
              data-height="input"
              data-length="input"
              onClick={() => {
                setShow((sh) => !sh)
                clearTimeout(blurTimeout)
              }}
            >
              <icon>{show ? 'lock_open' : 'lock'}</icon>
            </div>
          </Tooltip>
        </>
      }
      {...props}
      onBlur={onBlur}
    />
  )
})

export default Input
